'use client';

import { useEffect } from 'react';
import Link from 'next/link';
// DSN resolved from NEXT_PUBLIC_GLITCHREPLAY_DSN — see @/lib/glitchreplay-dsn.
import { GLITCHREPLAY_DSN } from '@/lib/glitchreplay-dsn';

/**
 * Next.js segment error boundary for /tools/*.
 *
 * Catches React render-time errors and uncaught throws inside any tool page
 * — these never hit window.onerror, so the GlobalErrorHandler in the root
 * layout misses them. This boundary closes that gap without requiring a
 * change to each individual tool.
 *
 * Reports to two places (matching the global pattern):
 *  1. /api/log-error — site's own D1 client_errors table
 *  2. GlitchReplay's Sentry-envelope endpoint — cross-site aggregator
 */
export default function ToolError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    const pageUrl =
      typeof window !== 'undefined' ? window.location.pathname : '/tools';
    const payload = {
      errorType: 'js_error' as const,
      message: error.message || 'Tool render error',
      stackTrace: error.stack,
      pageUrl,
    };
    // Defer reporting so React finishes commit before rrweb pauses; firing
    // synchronously here can race with reconciliation and surface as a null
    // parentNode crash inside replay.stop().
    const handle = setTimeout(() => {
      sendBeacon(
        '/api/log-error',
        JSON.stringify({ ...payload, source: error.digest ?? undefined }),
      );
      reportToGlitchReplay(payload);
    }, 0);
    return () => clearTimeout(handle);
  }, [error]);

  return (
    <div className="mx-auto max-w-2xl px-6 py-16 text-center">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Something went wrong with this tool
      </h2>
      <p className="mt-3 text-gray-600 dark:text-gray-400">
        We&apos;ve been notified and will look into it. Try again, or jump back
        to the tool index.
      </p>
      {error.digest && (
        <p className="mt-2 text-xs text-gray-400">Reference: {error.digest}</p>
      )}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <button
          onClick={reset}
          className="rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700"
        >
          Try again
        </button>
        <Link
          href="/tools"
          className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 font-medium text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
        >
          Browse other tools
        </Link>
      </div>
    </div>
  );
}

function sendBeacon(url: string, body: string, type = 'application/json') {
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, { method: 'POST', headers: { 'Content-Type': type }, body, keepalive: true })
      .catch(() => {});
  } catch {
    /* never throw from an error reporter */
  }
}

type ErrorPayload = {
  errorType: 'js_error' | 'unhandled_rejection' | 'resource_error';
  message: string;
  stackTrace?: string;
  pageUrl: string;
};

function reportToGlitchReplay(p: ErrorPayload) {
  try {
    const m = GLITCHREPLAY_DSN.match(/^https:\/\/([^@]+)@([^/]+)\/(.+)$/);
    if (!m) return;
    const [, key, host, pid] = m;
    const eventId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID().replace(/-/g, '')
        : Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    const frames = (p.stackTrace ?? '')
      .split('\n')
      .slice(1, 16)
      .map((line) => {
        const f = line.trim().match(/at\s+(?:(.+?)\s+\()?([^\s()]+):(\d+):(\d+)/);
        return f
          ? {
              function: f[1] || '<anonymous>',
              filename: f[2],
              lineno: Number(f[3]),
              colno: Number(f[4]),
              in_app: !f[2].includes('node_modules'),
            }
          : { function: line.trim() || '<anonymous>', in_app: false };
      });
    const env = [
      JSON.stringify({ event_id: eventId, sent_at: new Date().toISOString() }),
      JSON.stringify({ type: 'event' }),
      JSON.stringify({
        event_id: eventId,
        timestamp: Math.floor(Date.now() / 1000),
        platform: 'javascript',
        environment: process.env.NODE_ENV || 'production',
        tags: { surface: 'tool_error_boundary' },
        exception: {
          values: [
            {
              type: p.errorType,
              value: p.message,
              stacktrace: frames.length ? { frames } : undefined,
            },
          ],
        },
        request: { url: p.pageUrl, headers: { 'User-Agent': navigator.userAgent } },
      }),
    ].join('\n');
    sendBeacon(`https://${host}/api/${pid}/envelope/?sentry_key=${key}`, env, 'text/plain');
  } catch {
    /* never throw from an error reporter */
  }
}
